import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-form';
import moment from 'moment-timezone';
import PropTypes, { any } from 'prop-types';

import { CheckBox, DateTimePicker, Select } from '../../../components/Form';
import { required } from '../../../utils/validation';
import {
    allCourtsCheckboxKey,
    allowedTimePeriodIncrements,
    dateFormat,
    getSelectedCourtIds,
    hasInValidCourtSelection,
    snapDownToNearestIncrement,
} from '../../../utils/courtSchedular';
import { getDefaultFacilitiesSelected } from '../../../utils/dayPlanner';
import { getDayPlannerDayConfig } from '../../../api/tmsEventsDayPlannerApi';
import { getApiAxiosInstance } from '../../../utils/network';
import Popup from '../../../components/Popup';
import DayPlannerSidePanel from './DayPlannerSidePanel';

import styles from './dayPlanner.scss';
import { saveDayPlannerDayConfigData } from '../../../actions/day-planner-actions';

const DayConfigSidePanel = (props) => {
    const {
        className,
        closeDayConfigSidePanel,
        eventId,
        eventSchedule,
    } = props;

    const dispatch = useDispatch();
    const dayPlanner = useSelector(({ dayPlanner }) => dayPlanner);

    const [currentDayConfig, setCurrentDayConfig] = useState(null);
    const [dayConfigUpdateRequestParams, setDayConfigUpdateRequestParams] = useState({});
    const [confirmCourtChangeData, setConfirmCourtChangeData] = useState(false);
    const [dateToDisplay, setDateToDisplay] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
    };

    const defaultValues = useMemo(() => {
        if (currentDayConfig) {
            const { timeZone } = eventSchedule;
            const startTime = moment.utc(currentDayConfig.startTime).tz(timeZone?.value);

            return {
                startTime,
                incrementMinutes: snapDownToNearestIncrement(currentDayConfig.incrementMinutes),
                facilities: getDefaultFacilitiesSelected(currentDayConfig),
            };
        }

        return null;
    }, [currentDayConfig, eventSchedule]);

    useEffect(() => {
        if (currentDayConfig) {
            const dateTime = moment.utc(currentDayConfig.startTime).tz(eventSchedule?.timeZone?.value);
            setDateToDisplay(dateTime);
        }
    }, [eventSchedule, currentDayConfig]);

    useEffect(() => {
        const getDayConfig = async () => {
            const response = await getDayPlannerDayConfig(getApiAxiosInstance(), eventId, dayPlanner.viewState.day, dayPlanner.previousDay);
            setCurrentDayConfig(response.data);
        };

        if (!currentDayConfig && dayPlanner.viewState.day) {
            getDayConfig();
        }
    }, [currentDayConfig, eventId, eventSchedule, dayPlanner.viewState, dayPlanner.previousDay]);

    const hasMatchesScheduledForDay = currentDayConfig?.courts?.filter((court) => court.hasScheduledMatches)?.length > 0;

    const areDayDurationTimesChanged = (initialValues, values) => {
        return !initialValues.startTime.isSame(values.startTime);
    };

    const onFormSubmit = async (values, checkCourtIdsChange = true) => {
        const updatedValues = {
            day: currentDayConfig.day,
            startTime: values.startTime,
            incrementMinutes: values.incrementMinutes,
            confirmClearSchedule: false,
            courts: getSelectedCourtIds(values.facilities),
        };

        if (checkCourtIdsChange && currentDayConfig?.id) {
            let allCourtsWithMatchesAreEnabled = true;
            let allFacilitiesWithVirtualDrawsAreEnabled = true;

            currentDayConfig.facilities.forEach((facility) => {
                if (allFacilitiesWithVirtualDrawsAreEnabled && facility.hasVirtualDraws) {
                    allFacilitiesWithVirtualDrawsAreEnabled = values.facilities[facility.id].selected &&
                        Object.values(values.facilities[facility.id].courts).some((court) => court.selected);
                }

                facility.courts.forEach((court) => {
                    if (court.hasScheduledMatches && allCourtsWithMatchesAreEnabled) {
                        allCourtsWithMatchesAreEnabled = updatedValues.courts.includes(court.id);
                    }
                });
            });

            if (!allCourtsWithMatchesAreEnabled || !allFacilitiesWithVirtualDrawsAreEnabled) {
                setConfirmCourtChangeData(values);
                return;
            }
        }

        setDayConfigUpdateRequestParams(updatedValues);
        await dispatch(saveDayPlannerDayConfigData(eventId, dayPlanner.viewState.primaryView, updatedValues));
    };

    const onConfirmFormSubmission = () => {
        onFormSubmit(confirmCourtChangeData, false);
        setConfirmCourtChangeData(null);
    };

    const onCloseFormSubmissionConfirm = () => {
        setConfirmCourtChangeData(null);
    };

    const onConfirmClearSchedule = () => {
        const params = { ...dayConfigUpdateRequestParams, confirmClearSchedule: true };
        dispatch(saveDayPlannerDayConfigData(eventId, dayPlanner.viewState.primaryView, params));
        onCloseConfirmClearSchedule();
    };

    const onCloseConfirmClearSchedule = () => {
        dispatch({ type: 'SAVE_DAY_PLANNER_CONFIG_FAILURE' });
    };

    const handleDateChange = (fieldName, formApi) => (dateTime) => {
        const updatedTime = dateTime ? moment.utc(dateTime).tz(eventSchedule?.timeZone?.value) : null;
        setDateToDisplay(updatedTime);
        formApi.setValue(fieldName, updatedTime?.toISOString() || null);
    };

    const onAllCourtsCheckChange = (formApi, facility) => (value) => {
        facility.courts.forEach((court) => {
            formApi.setValue(['facilities', facility.id, 'courts', court.id, 'selected'], value);
        });
    };

    const onCourtCheckChange = (formApi, facility, court) => (value) => {
        const selectedCourts = facility.courts.filter((courtObj) => {
            if (courtObj.id === court.id) {
                return value;
            }

            return formApi.getValue(['facilities', facility.id, 'courts', courtObj.id, 'selected']);
        });

        formApi.setValue(['facilities', facility.id, 'courts', allCourtsCheckboxKey, 'selected'], selectedCourts.length === facility.courts.length);
    };

    if (!defaultValues) {
        return null;
    }

    return (
        <>
            <Form
                onSubmit={onFormSubmit}
                defaultValues={defaultValues}
            >
                {(formApi) => {
                    const { submitForm, getValue, errors, values } = formApi;

                    return (
                        <form onSubmit={submitForm} id="dayConfigForm" data-testid="day-planner.config.form">
                            <DayPlannerSidePanel
                                className={className}
                                closeSideBar={handleClose}
                                isVisible={isVisible}
                                onClose={closeDayConfigSidePanel}
                                data-testid="day-planner.config.panel"
                            >
                                <div className={styles.header} data-testid="day-planner.config.header">
                                    Set schedule
                                </div>
                                <div className={styles.content}>
                                    <p className={`mb0 ${styles.notice}`} data-testid="day-planner.config.notice">
                                        Please select a facility and courts to create your court schedule grid
                                    </p>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="day-planner.config.section-day-duration">
                                            Day & Duration
                                        </h6>
                                        <div className="row">
                                            <DateTimePicker
                                                id="dayConfigStartTime"
                                                field="startDateTimeUtc"
                                                dateLabel="Start date"
                                                timeLabel="Start time"
                                                selected={dateToDisplay}
                                                handleDateChange={handleDateChange('startTime', formApi)}
                                                themeV3
                                                defaultDate={dateToDisplay}
                                                dateFormat={dateFormat}
                                                disableDateSelector
                                            />
                                        </div>
                                        {hasMatchesScheduledForDay && areDayDurationTimesChanged(defaultValues, values) && (
                                            <p className={`${styles.notice}`} data-testid="day-planner.config.duration-change-notice">
                                                <span className={styles.highlight}>Note:</span> You have set different day duration times. All existing matches will auto shift to adhere to the new start time. Note: some matches may shift beyond the established end time.
                                            </p>
                                        )}
                                    </div>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="day-planner.config.section-increments">
                                            Time Period Increments
                                        </h6>
                                        <Select
                                            id="incrementMinutes"
                                            field="incrementMinutes"
                                            options={allowedTimePeriodIncrements}
                                            validate={required}
                                            hiddenLabel
                                            hiddenDefault
                                            themeV3
                                            data-testid="day-planner.config.increment-select"
                                        />
                                    </div>
                                    <div className={styles.section}>
                                        <h6 className={styles.sectionTitle} data-testid="day-planner.config.section-facility">
                                            Facility
                                        </h6>
                                        {currentDayConfig.facilities.length > 1 && (
                                            <>
                                                <p className={styles.sectionDescription} data-testid="day-planner.config.facility-description">
                                                    Which facilitie(s) would you like to use?
                                                </p>
                                                {currentDayConfig.facilities.map((facility) => {
                                                    return (
                                                        <div
                                                            key={`facility-${facility.id}`}
                                                            className={styles.facility}
                                                            data-testid={`day-planner.config.facility-${facility.id}`}
                                                        >
                                                            <CheckBox
                                                                id={`facilities${facility.id}selected`}
                                                                field={['facilities', facility.id, 'selected']}
                                                                customLabel={facility.name}
                                                                formGroupCssClass={styles.checkbox}
                                                                data-testid={`day-planner.config.facility-${facility.id}.checkbox`}
                                                            />
                                                            {getValue(['facilities', facility.id, 'selected']) && (
                                                                <>
                                                                    {facility.courts.length > 1 && (
                                                                        <CheckBox
                                                                            id={`facilities${facility.id}${allCourtsCheckboxKey}selected`}
                                                                            field={['facilities', facility.id, 'courts', allCourtsCheckboxKey, 'selected']}
                                                                            customLabel="All Courts"
                                                                            formGroupCssClass={`${styles.checkbox} ml32`}
                                                                            onChange={onAllCourtsCheckChange(formApi, facility)}
                                                                            data-testid={`day-planner.config.facility-${facility.id}.all-courts-checkbox`}
                                                                        />
                                                                    )}
                                                                    {facility.courts.map((court) => {
                                                                        return (
                                                                            <CheckBox
                                                                                key={`facility-${facility.id}-court-${court.id}`}
                                                                                id={`facilities${facility.id}${court.id}`}
                                                                                field={['facilities', facility.id, 'courts', court.id, 'selected']}
                                                                                customLabel={court.name}
                                                                                formGroupCssClass={`${styles.checkbox} ml32`}
                                                                                onChange={onCourtCheckChange(formApi, facility, court)}
                                                                                data-testid={`day-planner.config.facility-${facility.id}.court-${court.id}-checkbox`}
                                                                            />
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                        {currentDayConfig.facilities.length === 1 && (
                                            <>
                                                <p className={styles.sectionDescription} data-testid="day-planner.config.court-description">
                                                    Which court(s) would you like to use?
                                                </p>
                                                <h6 className={styles.sectionTitle} data-testid="day-planner.config.facility-name">
                                                    {currentDayConfig.facilities[0].name}
                                                </h6>
                                                <div
                                                    className={styles.facility}
                                                >
                                                    {currentDayConfig.facilities[0].courts?.length > 1 && (
                                                        <CheckBox
                                                            id={`facilities${currentDayConfig.facilities[0].id}${allCourtsCheckboxKey}selected`}
                                                            field={['facilities', currentDayConfig.facilities[0].id, 'courts', allCourtsCheckboxKey, 'selected']}
                                                            customLabel="All Courts"
                                                            formGroupCssClass={`${styles.checkbox}`}
                                                            onChange={onAllCourtsCheckChange(formApi, currentDayConfig.facilities[0])}
                                                            data-testid="day-planner.config.all-courts-checkbox"
                                                        />
                                                    )}
                                                    {currentDayConfig.facilities[0].courts?.map((court) => {
                                                        return (
                                                            <CheckBox
                                                                key={`facility-${currentDayConfig.facilities[0].id}-court-${court.id}`}
                                                                id={`facilities${currentDayConfig.facilities[0].id}${court.id}`}
                                                                field={['facilities', currentDayConfig.facilities[0].id, 'courts', court.id, 'selected']}
                                                                customLabel={court.name}
                                                                formGroupCssClass={`${styles.checkbox}`}
                                                                onChange={onCourtCheckChange(formApi, currentDayConfig.facilities[0], court)}
                                                                data-testid={`day-planner.config.court-${court.id}-checkbox`}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className={`${styles.footer} d-flex`}>
                                    <button
                                        className={`btn btn-default ${styles.submitButton} flex-1 mr8`}
                                        type="button"
                                        onClick={handleClose}
                                        data-testid="day-planner.config.cancel-btn"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={`btn btn-primary ${styles.submitButton} flex-1`}
                                        type="submit"
                                        disabled={dayPlanner.isSavingDayConfig || errors || hasInValidCourtSelection(values)}
                                        data-testid="day-planner.config.set-courts-btn"
                                    >
                                        {dayPlanner.isSavingDayConfig && (<i className="fas fa-circle-notch fa-spin" />)} Set courts
                                    </button>
                                </div>
                            </DayPlannerSidePanel>
                        </form>
                    );
                }}
            </Form>
            {dayPlanner.showConfirmClearSchedule && (
                <Popup
                    className="popup-sm"
                    popUpClassName={styles.confirmPopup}
                    onCloseClick={onCloseConfirmClearSchedule}
                    closeOnClickOuside={false}
                    config={{
                    fullScreen: false,
                    noPadding: true,
                    scrollable: false,
                    showCloseIcon: false,
                }}
                    data-testid="modal.day-planner.confirm-clear-schedule"
                >
                    <p className={styles.description} data-testid="modal.day-planner.confirm-clear-schedule.description">
                        Matches are scheduled on court for this date in the capacity simulator and/or created draws planner. Confirming this start time change will remove these matches from the planner, but your draws/divisions will remain and can be rescheduled with the new start time immediately afterward.
                    </p>
                    <p data-testid="modal.day-planner.confirm-clear-schedule.note">
                        <i>Note: This applies to matches in the capacity simulator and/or created draws planner for this date.</i>
                    </p>
                    <div className="d-flex mt16">
                        <button
                            className="btn btn-default flex-1 mr8"
                            type="button"
                            onClick={onCloseConfirmClearSchedule}
                            data-testid="modal.day-planner.confirm-clear-schedule.cancel-btn"
                        >
                        Cancel
                        </button>
                        <button
                            className="btn btn-primary flex-1"
                            type="button"
                            onClick={onConfirmClearSchedule}
                            data-testid="modal.day-planner.confirm-clear-schedule.confirm-btn"
                        >
                        OK
                        </button>
                    </div>
                </Popup>
            )}
            {confirmCourtChangeData && (
                <Popup
                    className="popup-sm"
                    popUpClassName={styles.confirmPopup}
                    onCloseClick={onCloseFormSubmissionConfirm}
                    closeOnClickOutside={false}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                        showCloseIcon: false,
                    }}
                    data-testid="modal.day-planner.confirm-court-change"
                >
                    <p className={styles.description} data-testid="modal.day-planner.confirm-court-change.description">
                        Matches are scheduled on the court you are unselecting for this day. If you confirm this change, all matches scheduled on that court will be removed from the schedule and must be re-scheduled
                    </p>
                    <div className="d-flex mt16">
                        <button
                            className="btn btn-default flex-1 mr8"
                            type="button"
                            onClick={onCloseFormSubmissionConfirm}
                            data-testid="modal.day-planner.confirm-court-change.cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary flex-1"
                            type="button"
                            onClick={onConfirmFormSubmission}
                            data-testid="modal.day-planner.confirm-court-change.confirm-btn"
                        >
                            OK
                        </button>
                    </div>
                </Popup>
            )}
        </>
    );
};

DayConfigSidePanel.propTypes = {
    className: PropTypes.string,
    closeDayConfigSidePanel: PropTypes.func.isRequired,
    eventId: PropTypes.number.isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
};

DayConfigSidePanel.defaultProps = {
    className: '',
};

export default DayConfigSidePanel;
